import { Amplify } from 'aws-amplify';
import { getAuthMode } from '../auth/authClient.js';

// unified-auth: the email-first login UX federates exclusively through
// BanyanSSO (Auth0) — the direct "Sign in with Okta" affordance is gone.
// BanyanSSO + native Cognito username/password are the only supported login
// paths; the legacy VITE_OKTA_ENABLED gate has been retired in favor of
// VITE_SSO_ENABLED for the frontend OAuth config.
const ssoEnabled = import.meta.env.VITE_SSO_ENABLED === 'true';
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
const region = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';

/**
 * Clear stale Amplify OAuth state from localStorage.
 * Amplify v6 stores PKCE code verifiers and OAuth state that can cause
 * 400 errors on page load if a previous OAuth flow was interrupted.
 */
function clearStaleOAuthState() {
  const url = new URL(window.location.href);
  const hasOAuthCode = url.searchParams.has('code');
  const hasOAuthError = url.searchParams.has('error');

  // Only clear if we're NOT in the middle of an OAuth callback
  if (!hasOAuthCode && !hasOAuthError) {
    // On the login page, clear ALL Cognito session data to prevent stale
    // email/password tokens (which lack OAuth scopes) from causing 400 errors.
    // This is safe because the login page is only for unauthenticated users —
    // authenticated users are redirected to "/" before reaching this code.
    const isLoginPage = url.pathname === '/login';
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.includes('oauthPKCE') || key.includes('oauthState') ||
          key.includes('oauth_state') || key.includes('amplifyOAuth')) {
        keysToRemove.push(key);
      }
      // On login page, also clear session tokens that may lack OAuth scopes
      if (isLoginPage && key.includes('CognitoIdentityServiceProvider')) {
        keysToRemove.push(key);
      }
    }
    if (keysToRemove.length > 0) {
      // Clear stale Cognito/OAuth state to prevent 400 errors
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  }
}

export function configureAmplify() {
  // local-dev-environment Phase 2 remediation (finding #5): local mode never
  // touches Amplify config at all — there is no Cognito user pool to
  // configure against, and any stray Amplify call would just error/no-op
  // loudly. `authClient`'s local branch handles auth entirely itself.
  if (getAuthMode() === 'local') {
    return;
  }

  const config = {
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      },
    },
  };

  // Add OAuth config when BanyanSSO federation is enabled
  if (ssoEnabled && cognitoDomain) {
    // Clear stale OAuth state BEFORE configure to prevent 400 errors
    clearStaleOAuthState();

    const currentOrigin = window.location.origin;
    config.Auth.Cognito.loginWith = {
      oauth: {
        domain: `${cognitoDomain}.auth.${region}.amazoncognito.com`,
        scopes: ['openid', 'email', 'profile'],
        redirectSignIn: [`${currentOrigin}/`],
        redirectSignOut: [`${currentOrigin}/login`],
        responseType: 'code',
        providers: [{ custom: 'BanyanSSO' }],
      },
    };

  }

  Amplify.configure(config);
}

export function isSsoEnabled() {
  return ssoEnabled && !!cognitoDomain;
}
